import React, { FC } from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { ChevronRightRounded } from "@mui/icons-material";
import { Link } from "gatsby-theme-material-ui";

import { Colors } from "@theme/colors";

interface AuthorProps {
  name: string;
  image?: string;
}

interface CardProps {
  title: string;
  link: string;
  description?: string;
  category?: string;
  author?: AuthorProps;
}

interface Props {
  articles: Array<CardProps>;
}

const Card: FC<CardProps> = ({
  title,
  link,
  description,
  category,
  author,
}) => {
  return (
    <Stack
      sx={{
        maxWidth: "100%",
        padding: "24px",
        borderRadius: "15px",
        backgroundColor: Colors.BlueGrey,
      }}
    >
      {author && (
        <Stack direction="row" mb={2}>
          {author.image && (
            <Avatar
              src={author.image}
              sx={{ marginRight: "8px", width: 24, height: 24 }}
            />
          )}
          {author.name && (
            <Typography
              variant="h6"
              sx={{
                fontSize: "14px",
                lineHeight: "22px",
                color: Colors.TextPrimary,
              }}
            >
              {author.name}
            </Typography>
          )}
        </Stack>
      )}
      <Link
        to={link}
        underline="none"
        sx={{
          cursor: "pointer",
        }}
      >
        <Typography
          variant="h5"
          mb={1}
          sx={{
            fontSize: "20px",
            lineHeight: "26px",
            fontWeight: "500",
            color: Colors.TextPrimary,
          }}
        >
          {title}
        </Typography>
      </Link>
      {description && (
        <Link
          to={link}
          underline="none"
          sx={{
            cursor: "pointer",
          }}
        >
          <Typography
            variant="body1"
            mb={1}
            sx={{
              height: { xs: "72px", md: "48px" },
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "300",
              color: Colors.TextPrimary,
              marginBottom: "24px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </Typography>
        </Link>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          {category && (
            <Typography
              variant="body1"
              component="span"
              sx={{
                height: "24px",
                padding: "0 8px",
                fontSize: "13px",
                fontWeight: "300",
                color: Colors.TextPrimary,
                backgroundColor: Colors.PrimaryContrast,
                borderRadius: "12px",
              }}
            >
              {category}
            </Typography>
          )}
        </Box>

        <Link
          to={link}
          underline="none"
          sx={{
            cursor: "pointer",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              color: Colors.TextPrimary,
              "&:hover": {
                color: Colors.PrimaryMain,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "20px",
                fontWeight: "300",
                color: Colors.TextPrimary,
                "&:hover": {
                  color: Colors.PrimaryMain,
                },
              }}
            >
              Read now
            </Typography>
            <ChevronRightRounded sx={{ fontSize: "18px" }} />
          </Stack>
        </Link>
      </Stack>
    </Stack>
  );
};

const ArticleCards: FC<Props> = ({ articles }) => {
  return (
    <Stack gap={2}>
      {articles.map((article: CardProps, idx: number) => {
        return (
          <Card
            title={article.title}
            link={article.link}
            author={article.author}
            description={article.description}
            category={article.category}
            key={`article-card-${idx}`}
          />
        );
      })}
    </Stack>
  );
};

export default ArticleCards;
